import { FC, useEffect, useRef, useState } from "react";

/**
 * 顶部 API / Skill 切换 Tab 栏
 */
export const SkillDocTabBar: FC<{
  active: "api" | "skill";
  onChange: (tab: "api" | "skill") => void;
}> = ({ active, onChange }) => {
  return (
    <div
      className="api-doc__lang-tabs skill-board-tabs"
      role="tablist"
      aria-label="文档板块"
    >
      <button
        type="button"
        role="tab"
        aria-selected={active === "api"}
        className={`api-doc__lang-tab${active === "api" ? " active" : ""}`}
        onClick={() => onChange("api")}
      >
        API
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={active === "skill"}
        className={`api-doc__lang-tab${active === "skill" ? " active" : ""}`}
        onClick={() => onChange("skill")}
      >
        Skill
      </button>
    </div>
  );
};

const REPO = "https://github.com/LycheeAILab/cine-sleuth";
const installPrompt = `请根据这个 GitHub 项目的官方安装说明，帮我安装或更新 cine-sleuth 到最新版本：${REPO}`;
const usagePrompt =
  "使用 CineSleuth 完整拉片这个视频，输出逐句台词、物理场景、逐镜表和视听分析。";

const external = (path: string, label: string) => (
  <a href={REPO + path} target="_blank" rel="noopener noreferrer">
    {label} ↗
  </a>
);

/**
 * Skill 提示词 / 安装提示词代码块，自带复制按钮
 */
const SkillCodeBlock: FC<{ title: string; text: string }> = ({
  title,
  text,
}) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  const onCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const input = document.createElement("textarea");
        input.value = text;
        input.style.cssText = "position:fixed;opacity:0";
        document.body.append(input);
        input.select();
        const ok = document.execCommand("copy");
        input.remove();
        if (!ok) throw new Error("copy failed");
      }
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="api-doc__code-blk skill-code">
      <div className="skill-code-heading">
        <strong>{title}</strong>
        <button type="button" className="api-doc__lang-tab skill-copy" onClick={onCopy}>
          {copied ? "已复制" : "复制"}
        </button>
      </div>
      <pre>{text}</pre>
    </div>
  );
};

/**
 * Skill 总览（overview）中间内容
 */
const OverviewContent: FC<{ onOpen: (id: string) => void }> = ({ onOpen }) => (
  <div className="skill-document" id="skill-overview">
    <section className="api-doc__hero">
      <div className="api-doc__hero-inner">
        <span className="api-doc__hero-kicker">✧ v1.0 正式版</span>
        <h1 className="api-doc__hero-title">API&amp;Skill 文档</h1>
        <p className="api-doc__hero-subtitle">
          使用 Codex / WorkBuddy 快速调用 Skill，助力开发者快速使用 Skill 业务能力。
        </p>
        <div className="api-doc__hero-baseurl">
          <span className="api-doc__hero-baseurl-label">适用工具</span>
          <span>Codex / WorkBuddy</span>
        </div>
      </div>
    </section>
    <section className="api-doc__guide-section">
      <div className="api-doc__guide-hd">
        <h2 className="api-doc__guide-title">核心能力</h2>
      </div>
      <div className="api-doc__api-groups">
        <div className="api-doc__api-group">
          <div className="api-doc__api-group-hd">
            <span className="api-doc__api-group-icon">🎬</span>
            <span className="api-doc__api-group-title">视频分析</span>
          </div>
          <div className="api-doc__api-group-grid">
            <a
              className="api-doc__api-card"
              href="#skill-cine-sleuth"
              onClick={(e) => {
                e.preventDefault();
                onOpen("skill-cine-sleuth");
              }}
            >
              <h3 className="api-doc__api-card-title">镜探</h3>
              <div className="api-doc__api-card-desc-wrap">
                <p className="api-doc__api-card-desc">
                  提取台词、拆解镜头、分析视听语言与内容结构，生成完整图文拉片报告。
                </p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

/**
 * 镜探 · CineSleuth 详情中间内容
 */
const CineSleuthContent: FC = () => (
  <div className="skill-document" id="skill-cine-sleuth">
    <div className="api-doc__guide-hd">
      <h2 className="api-doc__guide-title">镜探 · CineSleuth</h2>
      <p className="api-doc__guide-subtitle">
        把视频交给 Agent，用一句话描述分析目标。自动提取台词、重建场景、拆解镜头，并由当前
        Agent 整理完整的视听分析报告。
      </p>
    </div>

    <section className="api-doc__guide-section">
      <div className="api-doc__guide-hd">
        <h2 className="api-doc__guide-title">核心能力</h2>
        <p className="api-doc__guide-subtitle">
          从台词、镜头到内容结构，理解视频的每一处细节
        </p>
      </div>
      <p className="skill-cap-desc">
        适合电影片段、短片、访谈、口播和广告分析。可以完整拉片，也可以只关注台词字幕、导演摄影、剪辑节奏或短视频结构。
      </p>
      <table className="api-doc__tbl">
        <thead>
          <tr>
            <th>能力</th>
            <th>交付内容</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>台词提取</td>
            <td>逐句台词、说话人、语气、字幕差异与精确时间码</td>
          </tr>
          <tr>
            <td>逐镜拆解</td>
            <td>景别、角度、运动、转场、人物动作与画面文字</td>
          </tr>
          <tr>
            <td>场景与声音分析</td>
            <td>场景划分、人声、音乐、环境声、音效及声画关系</td>
          </tr>
          <tr>
            <td>结构分析</td>
            <td>开场钩子、节奏、信息密度与情绪推进</td>
          </tr>
          <tr>
            <td>镜头提示词</td>
            <td>逐镜生成可用于视频生成的中文提示词</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section className="api-doc__guide-section" id="skill-usage">
      <div className="api-doc__guide-hd">
        <h2 className="api-doc__guide-title">使用示例</h2>
        <p className="api-doc__guide-subtitle">提供视频后，将需要的提示词发送给 Agent</p>
      </div>
      <SkillCodeBlock title="完整拉片" text={usagePrompt} />
      <SkillCodeBlock
        title="台词与字幕"
        text="请用 CineSleuth 提取视频中的台词和屏幕文字，标注时间码；只整理可核对的信息。"
      />
      <SkillCodeBlock
        title="短视频与广告"
        text="请用 CineSleuth 分析这条视频如何吸引观众，重点拆解开场、信息组织、剪辑节奏和声音设计。"
      />
      <SkillCodeBlock
        title="导演与剪辑"
        text="请用 CineSleuth 分析这部短片的镜头安排，说明构图、摄影运动、转场和声音怎样配合叙事。"
      />
    </section>

    <section className="api-doc__guide-section" id="skill-delivery">
      <div className="api-doc__guide-hd">
        <h2 className="api-doc__guide-title">输入与交付</h2>
        <p className="api-doc__guide-subtitle">准备素材，了解你将获得的结果</p>
      </div>
      <table className="api-doc__tbl">
        <thead>
          <tr>
            <th>项目</th>
            <th>说明</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>视频来源</td>
            <td>本地视频或有权使用的抖音分享链接。平台限制导致链接无法下载时，可改用本地文件。</td>
          </tr>
          <tr>
            <td>时长范围</td>
            <td>五分钟以内；较长素材由 Agent 自动分段分析，并结合上下文整理完整结果。</td>
          </tr>
          <tr>
            <td>HTML 图文报告</td>
            <td>report.html 内嵌视觉段落首帧，可作为单文件离线阅读和分享。</td>
          </tr>
          <tr>
            <td>Markdown 报告</td>
            <td>report.md 与图片目录，便于后续编辑和归档。</td>
          </tr>
          <tr>
            <td>分析结果</td>
            <td>根据所选目标整理台词、场景、逐镜分析、声音、节奏及视频生成提示词。</td>
          </tr>
        </tbody>
      </table>
      <p>
        分析中断后可继续处理未完成的片段。最终报告默认在本地交付，也可按需选择归档到 Lab。
      </p>
    </section>

    <section className="api-doc__guide-section" id="skill-official-docs">
      <div className="api-doc__guide-hd">
        <h2 className="api-doc__guide-title">官方文档</h2>
        <p className="api-doc__guide-subtitle">详细配置与后续变化，请查阅官方项目</p>
      </div>
      <p className="skill-doc-links">
        {external("/blob/main/README.md", "查看完整 README")} ·{" "}
        {external("/blob/main/INSTALL.md", "Codex 安装说明")} ·{" "}
        {external("/blob/main/WORKBUDDY_INSTALL.md", "WorkBuddy 安装说明")}
      </p>
      <p className="skill-source">
        内容同步日期：2026-09-08 · 根据官方 README 2.0.0 功能说明整理；本页为使用摘要，后续更新以官方文档为准。
      </p>
    </section>
  </div>
);

/**
 * 右侧文档栏 —— Skill 总览时使用指南
 */
const SkillGuideRefPanel: FC = () => (
  <div className="api-doc__ref-panel">
    <div className="api-doc__ref-header">
      <div>
        <h4 className="api-doc__ref-title">Skill 使用指南</h4>
        <p className="api-doc__ref-subtitle">从选择能力到完成任务</p>
      </div>
    </div>
    <div className="api-doc__ref-section">
      <h4 className="api-doc__ref-section-title">1. 选择 Skill</h4>
      <p className="api-doc__ref-subtitle">
        从列表中选择适合当前任务的能力，查看支持的工具与使用场景。
      </p>
    </div>
    <div className="api-doc__ref-section">
      <h4 className="api-doc__ref-section-title">2. 按指引获取或更新</h4>
      <p className="api-doc__ref-subtitle">
        进入详情页，按照该 Skill 的官方说明完成配置。
      </p>
    </div>
    <div className="api-doc__ref-section">
      <h4 className="api-doc__ref-section-title">3. 描述你的任务</h4>
      <p className="api-doc__ref-subtitle">
        提供所需素材，用自然语言告诉 Agent 你希望得到的结果。
      </p>
    </div>
    <div className="api-doc__notice">
      不同 Skill 的适用工具与准备事项可能不同，请以各自详情页和官方说明为准。
    </div>
  </div>
);

/**
 * 右侧文档栏 —— 获取或更新 镜探 · CineSleuth（即用户引用的那段内容）
 */
const GetOrUpdateRefPanel: FC = () => (
  <div className="api-doc__ref-panel skill-reference" id="skill-install">
    <div className="api-doc__ref-header">
      <div>
        <h4 className="api-doc__ref-title">获取或更新</h4>
        <p className="api-doc__ref-subtitle">镜探 · CineSleuth</p>
      </div>
    </div>
    <p className="api-doc__ref-subtitle">
      将项目地址与下面的提示词发送给 Codex 或 WorkBuddy，由 Agent 按官方说明操作。
    </p>
    <SkillCodeBlock title="安装或更新提示词" text={installPrompt} />
    <div className="api-doc__notice">
      安装或更新完成后，新建一个任务或会话，即可加载最新版本。首次使用需登录或注册
      LycheeAI Lab 并完成授权。
    </div>
    <p className="skill-source">
      {external("", "GitHub 项目")} · {external("/blob/main/README.md", "完整 README")}
    </p>
  </div>
);

/**
 * Skill 文档页
 * 完全对齐 lychee-api-skills.js / 32.API&Skill文档.html 的生产结构：
 * - 左栏：Skill 总览 + 视频分析（镜探 · CineSleuth）分组导航，点击 = 在中间内容流中滚动定位到对应章节
 * - 中栏：总览 hero + 核心能力 + 镜探 · CineSleuth 完整章节（同一条可滚动内容流，向下滚动即可到达镜探）
 * - 右栏：Skill 使用指南 + 获取或更新 镜探 · CineSleuth（始终可见）
 */
export const SkillDocPage: FC<{
  activeTab: "api" | "skill";
  onTabChange: (tab: "api" | "skill") => void;
}> = ({ activeTab, onTabChange }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<"overview" | "cine-sleuth">(
    "overview"
  );
  const contentRef = useRef<HTMLDivElement | null>(null);

  // 滚动监听：根据中间内容流当前所在章节，高亮左栏并切换右栏面板
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const sections: {
      id: "overview" | "cine-sleuth";
      el: HTMLElement | null;
    }[] = [
      { id: "overview", el: content.querySelector<HTMLElement>("#skill-overview") },
      {
        id: "cine-sleuth",
        el: content.querySelector<HTMLElement>("#skill-cine-sleuth"),
      },
    ];
    const compute = () => {
      const cRect = content.getBoundingClientRect();
      const threshold = 140;
      let current: "overview" | "cine-sleuth" = "overview";
      for (const s of sections) {
        if (!s.el) continue;
        const top = s.el.getBoundingClientRect().top - cRect.top;
        if (top <= threshold) current = s.id;
      }
      setActiveSection(current);
    };
    content.addEventListener("scroll", compute, { passive: true });
    compute();
    return () => content.removeEventListener("scroll", compute);
  }, []);

  // 左栏点击：在中间内容流中平滑滚动到对应章节（而非切换视图）
  const scrollToId = (id: string) => {
    const content = contentRef.current;
    if (!content) return;
    const el = content.querySelector<HTMLElement>("#" + id);
    if (!el) return;
    const top =
      el.getBoundingClientRect().top -
      content.getBoundingClientRect().top +
      content.scrollTop;
    content.scrollTo({ top: Math.max(0, top - 8), behavior: "smooth" });
  };

  const q = searchQuery.trim().toLowerCase();
  const matchCine = !q || "镜探 cinesleuth 视频分析".includes(q);

  return (
    <div className="api-doc__main api-doc__main--skill">
      <aside
        className={`api-doc__sidebar-left${mobileNavOpen ? " mobile-open" : ""}`}
      >
        <div className="api-doc__sb-top">
          <div className="api-doc__sb-search">
            <input
              type="text"
              placeholder="搜索 Skill…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="api-doc__tree">
          <div className="api-doc__grp">
            <div className="api-doc__grp-items">
              <a
                className={`api-doc__t-item${activeSection === "overview" ? " active" : ""}`}
                href="#skill-overview"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("skill-overview");
                }}
              >
                <span className="api-doc__lbl">Skill 总览</span>
              </a>
            </div>
          </div>
          {matchCine && (
            <div className="api-doc__grp">
              <div className="api-doc__grp-title">
                <span className="api-doc__group-emoji">🎬</span>
                <span className="api-doc__group-name">视频分析</span>
                <span className="api-doc__group-count">1</span>
              </div>
              <div className="api-doc__grp-items">
                <a
                  className={`api-doc__t-item${activeSection === "cine-sleuth" ? " active" : ""}`}
                  href="#skill-cine-sleuth"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToId("skill-cine-sleuth");
                  }}
                >
                  <span className="api-doc__lbl">镜探 · CineSleuth</span>
                  <span className="api-doc__nav-method">SKILL</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </aside>

      <main className="api-doc__content" ref={contentRef}>
        {/* 顶部 API / Skill 切换 Tab 栏：位于 hero 正上方，随内容滚动（非固定常驻） */}
        <SkillDocTabBar active={activeTab} onChange={onTabChange} />
        <OverviewContent onOpen={scrollToId} />
        <CineSleuthContent />
      </main>

      {/* 右侧文档栏：随中间内容流当前章节切换 */}
      <aside className="api-doc__sidebar-right">
        <div className="api-doc__sb-r-inner">
          {activeSection === "overview" ? (
            <SkillGuideRefPanel />
          ) : (
            <GetOrUpdateRefPanel />
          )}
        </div>
      </aside>
    </div>
  );
};
